import { Dictionary, groupBy, isEmpty, reject } from 'lodash';
import moment, { duration, Moment } from 'moment';
import { IntlShape } from 'react-intl';
import { AccountInitDataProjectsQuery } from 'api/account';
import { Holiday } from 'api/holiday-calendar/types';
import { Company, User, UserId, UserStatus } from 'api/models';
import { Project } from 'api/project';
import {
  CustomFieldValueSourceType,
  Field,
  FieldAccessMetaInBase3,
  FieldValueType,
} from 'api/tasks';
import {
  Configuration,
  EnabledSourcesConfiguration,
  ProjectReportTimeSettings,
} from 'api/timesheets/configuration/configuration';
import {
  SubmissionHoursConfiguration,
  SubmissionHoursConstrainType,
} from 'api/timesheets/configuration/submission-hours';
import type { ProjectInUserProfile } from 'api/user';
import { withPredicatedParseError } from 'components/atoms/input-duration';
import { CustomOption } from 'components/atoms/select/types';
import {
  FieldDataType,
  InheritableFieldValue,
} from 'components/organisms/module-settings/fields/types';
import {
  Minutes,
  WorkingDaysConfiguration,
} from 'components/organisms/user-capacity/types';
import { NON_PROJECT_ACTIVITY_ID } from 'constant/timesheet';
import { date as dateByDay, DayOfWeek, getStartOfWeek } from 'core/day-of-week';
import { getDays } from 'utils/date-field/get-days-array';
import {
  hasReadAccessForField,
  hasWriteAccessForField,
} from 'utils/field-access';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasHoliday } from 'utils/holiday-calendar-utils';
import { sortByText } from 'utils/sort-by-text';
import { sortByUserStatus } from 'utils/sort-by-user-status';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { unreachable } from 'utils/unreachable';
import { isNonWorkingDay } from 'utils/working-days';
import {
  FilterViewType,
  PolicyConflictPreview,
  TimeEntryState,
  TimesheetProject,
  TimesheetReminder,
  TimesheetStatus,
  TimesheetTableTimeEntry,
  TimesheetWeeklyRow,
  UserLevelSummary,
  UserTimesheet,
  WeekPreset,
} from '../types';

export const getUpdatedFieldsPayload = (
  existingFields: Array<InheritableFieldValue<FieldValueType>>,
  newFields: Array<InheritableFieldValue<FieldValueType>>,
  customFields: Field[] | undefined = [],
): Array<InheritableFieldValue<FieldValueType>> => {
  // setting values in Map with fieldId being key
  const existingFieldsMap = new Map(
    existingFields?.map((field) => [field.fieldId, field]),
  );

  const newFieldsMap = new Map(
    newFields?.map((field) => [field.fieldId, field]),
  );

  return customFields.reduce<Array<InheritableFieldValue<FieldValueType>>>(
    (acc, actualField) => {
      const newField = newFieldsMap.get(actualField.fieldId);
      const existingField = existingFieldsMap.get(actualField.fieldId);

      // if the fieldValue has changed in the UI or the field is mandatory, update time-entry payload should always include these
      // if a new field has been added, it should be included in the payload
      if (actualField.metaData?.isMandatory && existingField) {
        acc.push({
          fieldId: actualField.fieldId,
          fieldValue: newField?.fieldValue ?? existingField.fieldValue,
        } as InheritableFieldValue<FieldValueType>);
      } else if (
        newField &&
        existingField?.fieldValue !== newField.fieldValue
      ) {
        acc.push(newField);
      }

      return acc;
    },
    [],
  );
};

export const getReadableTimeEntryFields = (
  timeEntryCustomFields: Field[] | undefined,
  fieldAccessMetaInBase3: FieldAccessMetaInBase3 | undefined,
) => {
  return timeEntryCustomFields?.filter(
    (f) =>
      f.isEnabled &&
      (hasReadAccessForField(f, fieldAccessMetaInBase3) ||
        (f.metaData?.isMandatory &&
          f.metaData?.fieldValueSourceType ===
            CustomFieldValueSourceType.Manual)),
  );
};

export const getWritableTimeEntryFields = (
  timeEntryCustomFields: Field[] | undefined,
  fieldAccessMetaInBase3: FieldAccessMetaInBase3 | undefined,
) => {
  return timeEntryCustomFields?.filter(
    (f) =>
      f.isEnabled &&
      (hasWriteAccessForField(f, fieldAccessMetaInBase3) ||
        (f.metaData?.isMandatory &&
          f.metaData?.fieldValueSourceType ===
            CustomFieldValueSourceType.Manual)),
  );
};

export {
  getDateAfterSomeNumberOfWorkingDays,
  getEndDateForWorkingDays,
  isNonWorkingDay,
  numberOfWorkingDaysBetweenDates,
} from 'utils/working-days';

export const getColumnClassesForTimeCell = (
  date: Moment,
  workingDays: WorkingDaysConfiguration,
  activeColumn?: string,
  currentUser?: User,
  holidays?: Holiday[],
): string => {
  const classNames: string[] = [];

  if (isNonWorkingDay(date, workingDays)) {
    classNames.push('weekend-cell');
  }
  if (date.day() !== 0 && date.day() !== 6) {
    if (checkIfCurrentDateHasTimeOff(currentUser?.userTimeOffDetails, date)) {
      classNames.push('timeoff-cell');
    }
    if (checkIfCurrentDateHasHoliday(holidays, date)) {
      classNames.push('holiday-cell');
    }
  }
  if (moment().isSame(date, 'day')) {
    classNames.push('today-cell');
  }
  if (activeColumn === date.format('YYYY-MM-DD')) {
    classNames.push('active-column');
  }

  return classNames.join(' ');
};

export const getCellClassesForTimesheetCell = (
  date: Moment,
  record: UserLevelSummary,
): string => {
  const classNames: string[] = [];

  if (
    record.status !== TimesheetStatus.NotSubmitted &&
    checkIfCurrentDateHasTimeOff(record.user?.userTimeOffDetails, date)
  ) {
    classNames.push('timeoff-cell');
  }

  if (record.status === TimesheetStatus.NotSubmitted) {
    classNames.push('non-submitted-cell');
  } else {
    classNames.push('hoverable-cell');
  }

  return classNames.join(' ');
};

export const getTotalMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number => (entries ?? []).reduce((total, entry) => total + entry.minutes, 0);

export const getTotalMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) => total + getTotalMinutesFromEntries(childEntries),
    0,
  );

export const getTotalBillableMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number =>
  entries.reduce(
    (total, entry) => (entry.billable ? total + entry.minutes : total),
    0,
  );

export const getTotalBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) =>
      total + getTotalBillableMinutesFromEntries(childEntries),
    0,
  );

export const getTotalNonBillableMinutesFromEntries = (
  entries: TimesheetTableTimeEntry[],
): number =>
  entries.reduce(
    (total, entry) => (!entry.billable ? total + entry.minutes : total),
    0,
  );

export const getTotalNonBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
): number =>
  Object.values(days).reduce(
    (total, childEntries) =>
      total + getTotalNonBillableMinutesFromEntries(childEntries),
    0,
  );

export const getTotalBillableNonBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
) => {
  const totalMinutes = getTotalMinutesFromEntries(entries) || 0;
  const totalBillableMinutes = getTotalBillableMinutesFromEntries(entries) || 0;

  const totalNonBillableMinutes =
    getTotalNonBillableMinutesFromEntries(entries) || 0;

  return { totalMinutes, totalBillableMinutes, totalNonBillableMinutes };
};

export const getTotalBillableNonBillableMinutesForTask = (
  days: Record<string, TimesheetTableTimeEntry[]>,
) => {
  const totalBillableHours = getTotalBillableMinutesForTask(days);

  const totalNonBillableHours = getTotalNonBillableMinutesForTask(days);

  return {
    totalBillableHours,
    totalNonBillableHours,
  };
};

export const getTotalBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
): number =>
  getTotalMinutesFromEntries(entries.filter((entry) => entry.billable));

export const getTotalNonBillableMinutes = (
  entries: TimesheetTableTimeEntry[],
): number =>
  getTotalMinutesFromEntries(entries.filter((entry) => !entry.billable));

export const calculateUtilization = (
  billableMinutes: number,
  capacityMinutes: Minutes,
): number =>
  capacityMinutes === 0 ? -1 : (billableMinutes / capacityMinutes) * 100;

export const calculatePercentage = (
  minutes: number,
  billableMinutes: number,
  nonBillableMinutes: number,
): number => {
  const total = billableMinutes + nonBillableMinutes;

  return total === 0 ? 0 : (minutes / total) * 100;
};

const hideInactiveUser = (timesheet: UserTimesheet): boolean => {
  return (
    timesheet?.user?.userStatus === UserStatus.Inactive &&
    timesheet?.entries?.length === 0
  );
};

export const getTimesheetsByFilter = (
  allTimesheets: UserTimesheet[],
  filter: FilterViewType,
  listOfUsersMappedUnderYou: number[],
  listOfProjectsIOwn: number[],
): UserTimesheet[] => {
  const timesheets = allTimesheets.filter(
    (timesheet) => !hideInactiveUser(timesheet),
  );

  switch (filter) {
    case FilterViewType.Approved:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Approved,
      );
    case FilterViewType.Submitted:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Submitted,
      );
    case FilterViewType.NotSubmitted:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.NotSubmitted,
      );
    case FilterViewType.Rejected:
      return timesheets.filter(
        (timesheet) => timesheet.status === TimesheetStatus.Rejected,
      );
    case FilterViewType.MyTeam:
      return timesheets.filter((timesheet) =>
        listOfUsersMappedUnderYou.includes(timesheet?.user?.userId),
      );
    case FilterViewType.ProjectsIOwn:
      return timesheets.filter((timesheet) =>
        listOfProjectsIOwn.includes(timesheet.project?.id),
      );
    default:
      return timesheets;
  }
};
export const getCountOfTimesheetsByFilter = (
  timesheets: UserTimesheet[],
  filter: FilterViewType,
  listOfUsersMappedUnderYou: number[],
  listOfProjectsIOwn: number[],
): number =>
  (
    getTimesheetsByFilter(
      timesheets,
      filter,
      listOfUsersMappedUnderYou,
      listOfProjectsIOwn,
    ) ?? []
  ).length;

export const getWeekStartDate = (week: string): Moment => {
  switch (week as WeekPreset) {
    case WeekPreset.ThisWeek:
      return getStartOfWeek(DayOfWeek.Monday, moment());
    case WeekPreset.PreviousWeek:
      return getStartOfWeek(DayOfWeek.Monday, moment().subtract(1, 'week'));
    default:
      return getStartOfWeek(DayOfWeek.Monday, moment(week, 'YYYY-MM-DD'));
  }
};

export const getWeekPreset = (
  startDate: Moment,
  allowedPresets: WeekPreset[],
): string => {
  return (
    allowedPresets
      .map((preset): [WeekPreset, Moment] => [
        preset,
        getWeekStartDate(preset as string),
      ])
      .find(([_, presetDate]) => presetDate.isSame(startDate, 'day'))?.[0] ??
    startDate.format('YYYY-MM-DD')
  );
};

export const getTimesheetUrl = ({
  currentUserId,
  userId,
  startDate,
  projectId,
}: {
  currentUserId: UserId;
  userId?: UserId;
  startDate?: string;
  projectId?: number;
}): string => {
  // currentUserId/userId are branded tuples `['user_id', number]` but
  // callers occasionally pass a raw number (legacy notification payloads).
  // Normalize both before use so a number doesn't throw "is not iterable"
  // or produce `/approvals/undefined` from `userId[1]`.
  const _currentUserId = Array.isArray(currentUserId)
    ? currentUserId[1]
    : currentUserId;
  const _userId = Array.isArray(userId) ? userId[1] : userId;

  if (_userId === undefined || _currentUserId === _userId) {
    return `/timesheets/${startDate || WeekPreset.ThisWeek}/my-timesheet`;
  }

  if (projectId) {
    return `/timesheets/${
      startDate || WeekPreset.ThisWeek
    }/approvals/${_userId}?projectId=${projectId}`;
  }

  return `/timesheets/${startDate || WeekPreset.ThisWeek}/approvals/${_userId}`;
};

export const isTimeEntryNotLoaded = (entry: TimesheetTableTimeEntry): boolean =>
  entry.inState !== TimeEntryState.Loaded;

export const isTimeEntryInCreating = (
  entry: TimesheetTableTimeEntry,
): boolean => entry.inState === TimeEntryState.Creating;

export const isMandatoryCustomFieldEmpty = (
  val: TimesheetTableTimeEntry,
  timeEntryCustomFields: Field[],
  fieldAccessMetaInBase3: FieldAccessMetaInBase3 | undefined,
) => {
  const fieldsToShow = getReadableTimeEntryFields(
    timeEntryCustomFields,
    fieldAccessMetaInBase3,
  );

  const mandatoryFields = fieldsToShow?.filter(
    (field) =>
      field.isEnabled &&
      field.metaData?.isMandatory &&
      field.fieldDataType !== FieldDataType.Boolean,
  );

  if (mandatoryFields?.length) {
    const hasEmptyMandatoryField = mandatoryFields.some((field) => {
      const fieldValue = val.fields?.find(
        (f) => f.fieldId === field.fieldId,
      )?.fieldValue;

      return (
        fieldValue === null || fieldValue === undefined || fieldValue === ''
      );
    });

    if (hasEmptyMandatoryField) {
      return true;
    }
  }

  return false;
};

export const isTimeEntryPartiallyFilled = (
  entry: TimesheetTableTimeEntry,
  mandateCategory: boolean,
  shouldValidateBudget: boolean = false,
): boolean =>
  entry.inState === TimeEntryState.PartiallyFilled ||
  (mandateCategory && (!entry.category || isEmpty(entry.category))) ||
  (shouldValidateBudget && !entry.projectFinancialsBudgetId);

export const withLessThanOneDayParser = withPredicatedParseError<
  number,
  'greater_than_one_day'
>('greater_than_one_day', (x) => x > duration(1, 'day').asMinutes());

export const mondayOfWeek = (value: Moment = moment()): Moment =>
  dateByDay(DayOfWeek.Monday, value);

export const mondayOfWeekForApproval = (currDay): Moment => {
  if (currDay.format('dddd') === DayOfWeek.Sunday) {
    return currDay.subtract(6, 'days');
  }

  return dateByDay(DayOfWeek.Monday, currDay);
};

export const endOfWeek = (day: Moment): Moment =>
  dateByDay(DayOfWeek.Monday, day).add(6, 'days');

export const weekFormatter = (currentValue: moment.MomentInput): string => {
  return `${mondayOfWeek(moment(currentValue)).format(
    'DD MMM YYYY',
  )} - ${endOfWeek(moment(currentValue)).format('DD MMM YYYY')}`;
};

export const checkForProjectStatus = (
  option,
  projectReportTimeSettings: ProjectReportTimeSettings,
  isResourceManagement?: boolean,
) => {
  if (
    projectReportTimeSettings.projectWithStatuses.length === 0 ||
    isResourceManagement ||
    !option.statusValue
  ) {
    return true;
  }

  return projectReportTimeSettings.projectWithStatuses.includes(
    option.statusValue,
  );
};

export const getProjectSelectOptions = (
  projectOptions,
  myProjectIds: number[],
  translate,
  projectReportTimeSettings?: ProjectReportTimeSettings,
  isResourceManagement?: boolean,
) => {
  const byNonProject = (option) => option.value === NON_PROJECT_ACTIVITY_ID;
  const [nonProject] = projectOptions?.filter(byNonProject) ?? [];
  const otherProjects = reject(projectOptions, byNonProject).filter(
    (projectOption) =>
      projectReportTimeSettings
        ? checkForProjectStatus(
            projectOption,
            projectReportTimeSettings,
            isResourceManagement,
          )
        : true,
  );

  const predicateByMyProject = (projectValue: CustomOption<Project>): boolean =>
    myProjectIds.includes(projectValue.value as number);

  const projectOption = {
    id: 'projects',
    label: translate('other_project'),
    options: otherProjects.filter(
      (projectValue) => !predicateByMyProject(projectValue),
    ),
  };
  const nonProjectOption = {
    id: 'nonProject',
    options: nonProject ? [nonProject] : [],
  };

  const yourProject = {
    id: 'yourProject',
    label: translate('your_project'),
    options: otherProjects.filter((projectValue) =>
      predicateByMyProject(projectValue),
    ),
  };

  if (isResourceManagement) {
    return [nonProjectOption, yourProject, projectOption];
  }

  const projectSelectOptions = projectReportTimeSettings?.yourProjectOnly
    ? [nonProjectOption, yourProject]
    : [nonProjectOption, yourProject, projectOption];

  return projectSelectOptions;
};

export const getLatestReminderAt = (reminders: TimesheetReminder[]) => {
  if (!reminders) {
    return 0;
  }
  const sorted = reminders
    .map((reminder) => reminder.createdAt)
    .sort((a, b) => b - a);

  return sorted.length > 0 ? sorted[0] : 0;
};

const groupEntriesByDay = (
  entries: TimesheetTableTimeEntry[],
  isManagerApproval: boolean,
) => {
  const groupedEntry = groupBy(entries, 'date');

  return Object.keys(groupedEntry).reduce((days, entryDate) => {
    days[entryDate] = isManagerApproval
      ? { totalMinutes: getTotalMinutesFromEntries(groupedEntry[entryDate]) }
      : getTotalBillableNonBillableMinutes(groupedEntry[entryDate]);

    return days;
  }, {});
};

export const calculateUserLevelSummaries = (
  userTimesheets: UserTimesheet[],
  isManagerApproval: boolean,
): UserLevelSummary[] => {
  const userLevelSummaries = userTimesheets.reduce((acc, userTimesheetNext) => {
    const invalidData = hideInactiveUser(userTimesheetNext);

    if (!invalidData) {
      const policyConflicts = userTimesheetNext?.entries?.reduce(
        (policyConflictAcc, entry) => {
          if (entry.policyConflicts) {
            entry.policyConflicts.forEach((conflict) => {
              policyConflictAcc[conflict.policyId] = conflict;
            });
          }

          return policyConflictAcc;
        },
        {} as Dictionary<PolicyConflictPreview>,
      );

      acc.push({
        user: userTimesheetNext.user,
        userId: userTimesheetNext?.user?.userId,
        startDate: userTimesheetNext.startDate,
        status: userTimesheetNext.status,
        updatedAt: userTimesheetNext.updatedAt,
        lastReminderAt:
          userTimesheetNext.status === TimesheetStatus.NotSubmitted
            ? getLatestReminderAt(userTimesheetNext.reminders)
            : 0,
        days: groupEntriesByDay(userTimesheetNext.entries, isManagerApproval),
        isAnyEntryInvoiced: (userTimesheetNext?.entries ?? []).some(
          (x) => x.invoiceId != null,
        ),
        projectId: userTimesheetNext.project?.id,
        projectName: userTimesheetNext.project?.name,
        reminders:
          userTimesheetNext.status === TimesheetStatus.NotSubmitted
            ? userTimesheetNext.reminders ?? []
            : [],
        policyConflicts,
        // checks if any entry is there which is in submitted / approved / rejected status
        isAnyEntrySubmitted: (userTimesheetNext?.entries ?? []).some((x) => {
          return (
            x.approvalStatus != null && x.approvalStatus !== 'NOT_SUBMITTED'
          ); // Not using enum because importing that causes circular dependency 🤷🏻‍♀️
        }),
      });
    }

    return acc;
  }, [] as Array<UserLevelSummary>);
  const ordered = (timesheets) => {
    return (timesheets ?? []).sort((timesheetA, timesheetB) =>
      sortByText(fullName(timesheetA.user), fullName(timesheetB.user)),
    );
  };

  const grouped = groupBy(userLevelSummaries, 'status');

  const orderedUserTimesheets: UserLevelSummary[] = [
    ...ordered(grouped[TimesheetStatus.Submitted]),
    ...ordered(grouped[TimesheetStatus.Rejected]),
    ...ordered(grouped[TimesheetStatus.Approved]),
    ...ordered(grouped[TimesheetStatus.NotSubmitted]),
  ];

  const sortedUserSummaries = orderedUserTimesheets.sort(sortByUserStatus);

  return sortedUserSummaries;
};

const currentWeekDays = (startDate: Moment) =>
  getDays(startDate, 7).map((x) => x.format('YYYY-MM-DD'));

export const isEntireWeekFreezed = (
  startDate: Moment,
  configuration: Configuration,
) => {
  if (configuration.lockSetting.freeze.enabled) {
    return moment(currentWeekDays(startDate)[6]).isSameOrBefore(
      moment(configuration.lockSetting.freeze.enableAfter),
    );
  }

  return false;
};

export const isDayFreezedInWeek = (
  configuration: Configuration,
  startDate: Moment,
) => {
  if (configuration.lockSetting.freeze.enabled) {
    return currentWeekDays(startDate).includes(
      configuration.lockSetting.freeze.enableAfter ?? '',
    );
  }

  return false;
};

export const renderWeekDate = (day: Moment, format = 'DD MMM YY'): string => {
  return `${mondayOfWeek(day).format(format)} - ${endOfWeek(day).format(
    format,
  )}`;
};

export const getPlaceHolderText = (
  timesheetEntriesConfig: EnabledSourcesConfiguration,
  intl: IntlShape,
) => {
  const options: string[] = [];

  if (timesheetEntriesConfig.PHASE) {
    options.push(intl.formatMessage({ id: 'phase' }));
  }
  if (timesheetEntriesConfig.TASK || timesheetEntriesConfig.MILESTONE) {
    options.push(intl.formatMessage({ id: 'task' }));
  }
  if (timesheetEntriesConfig.ADHOC) {
    options.push(
      intl.formatMessage({ id: 'timesheet.add.placeholder.options.activity' }),
    );
  }

  const formattedList = intl.formatList(options, { type: 'unit' });

  return intl.formatMessage(
    { id: 'timesheets.buttons.add_phase_task_activity' },
    { items: formattedList },
  );
};

export { isNonBillableHoursFlagEnabled } from 'api/timesheets/non-billable-hours-flag';

/**
 * Constructs AccountInitDataProjectsQuery from UserTimesheet array.
 * Extracts project data including customer information from timesheet entries.
 *
 * @param timesheets - Array of user timesheets containing project information
 * @returns AccountInitDataProjectsQuery with projects array and byId lookup
 */
export const constructProjectsDataFromTimesheets = (
  timesheets: Array<{
    project: TimesheetProject;
  }>,
): AccountInitDataProjectsQuery => {
  const { projects, byId } = timesheets.reduce<{
    projects: ProjectInUserProfile[];
    byId: Dictionary<ProjectInUserProfile>;
  }>(
    (acc, { project }) => {
      if (project.id === NON_PROJECT_ACTIVITY_ID) {
        return acc;
      }

      const entry = {
        projectId: project.id,
        projectName: project.name || '',
        customer: {
          companyLogoUrl: project?.logoUrl,
          companyName: project?.companyName,
        },
        deleted: project?.deleted ?? false,
        archived: project?.archived ?? false,
      } as unknown as ProjectInUserProfile;

      acc.projects.push(entry);
      acc.byId[project.id] = entry;

      return acc;
    },
    { projects: [], byId: {} },
  );

  return {
    projects,
    byId,
  };
};

export const constructTimesheetProjects = (
  timesheetRows: TimesheetWeeklyRow[],
  projectOptions?: CustomOption[],
): AccountInitDataProjectsQuery | undefined => {
  const projectsById = timesheetRows.reduce<Dictionary<ProjectInUserProfile>>(
    (acc, row) => {
      const project = row.task?.project;

      if (project?.id === NON_PROJECT_ACTIVITY_ID) {
        return acc;
      }

      if (project?.id && !acc[project.id]) {
        acc[project.id] = {
          projectId: project.id,
          projectName: project.name,
          customer: {
            companyLogoUrl: project.customer?.companyLogoUrl,
          } as Company,
          deleted: project.deleted,
          archived: project.archived,
        } as ProjectInUserProfile;
      }

      return acc;
    },
    {},
  );

  // Also include projects from projectOptions (active projects for selection)
  projectOptions?.reduce((acc, option) => {
    const projectId = Number(option.value);

    if (projectId && !acc[projectId]) {
      acc[projectId] = {
        projectId,
        projectName: option.label,
        customer: {
          companyLogoUrl: option.logoUrl,
        } as Company,
        deleted: false,
        archived: false,
      } as ProjectInUserProfile;
    }

    return acc;
  }, projectsById);

  return {
    byId: projectsById,
    projects: Object.values(projectsById),
  };
};

export const submissionHoursConstraintMeet = (
  submissionHoursConfiguration: SubmissionHoursConfiguration,
  totalMinutesFromTimesheet: number,
  userCapacityLimit: number,
): boolean => {
  switch (submissionHoursConfiguration.constraintType) {
    case SubmissionHoursConstrainType.IsEmpty:
      return true;
    case SubmissionHoursConstrainType.LessThan:
      return (
        totalMinutesFromTimesheet <=
        duration(userCapacityLimit, 'hours').asMinutes()
      );
    case SubmissionHoursConstrainType.GreaterThan:
      return (
        totalMinutesFromTimesheet >=
        duration(userCapacityLimit, 'hours').asMinutes()
      );
    case SubmissionHoursConstrainType.Equals:
      return (
        totalMinutesFromTimesheet ===
        duration(userCapacityLimit, 'hours').asMinutes()
      );
    default:
      return unreachable(submissionHoursConfiguration);
  }
};

export const getUserCapacityLimit = (
  submissionHoursConfiguration: SubmissionHoursConfiguration,
  userCapacity: number,
) => {
  if (
    submissionHoursConfiguration.constraintType ===
    SubmissionHoursConstrainType.IsEmpty
  ) {
    return 0;
  }

  if (
    submissionHoursConfiguration.constraintType ===
    SubmissionHoursConstrainType.GreaterThan
  ) {
    return Math.min(submissionHoursConfiguration.value, userCapacity);
  }

  return submissionHoursConfiguration.value;
};
